import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import { 
  Plus, 
  Search, 
  Users, 
  Edit, 
  Trash2, 
  Loader2,
  User
} from "lucide-react";

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    phone: "",
    email: "",
    address: "",
    insurance_member_id: "",
    insurance_policy_number: "",
    insurance_provider: ""
  });

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/patients`, { withCredentials: true });
      setPatients(response.data);
    } catch (error) {
      toast.error("Failed to fetch patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPatient) {
        await axios.put(`${API}/patients/${editingPatient.patient_id}`, formData, { withCredentials: true });
        toast.success("Patient updated successfully");
      } else {
        await axios.post(`${API}/patients`, formData, { withCredentials: true });
        toast.success("Patient added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save patient");
    }
  };

  const handleEdit = (patient) => {
    setEditingPatient(patient);
    setFormData({
      first_name: patient.first_name || "",
      last_name: patient.last_name || "",
      date_of_birth: patient.date_of_birth || "",
      gender: patient.gender || "",
      phone: patient.phone || "",
      email: patient.email || "",
      address: patient.address || "",
      insurance_member_id: patient.insurance_member_id || "",
      insurance_policy_number: patient.insurance_policy_number || "",
      insurance_provider: patient.insurance_provider || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (patientId) => {
    if (!window.confirm("Are you sure you want to delete this patient?")) return;
    try {
      await axios.delete(`${API}/patients/${patientId}`, { withCredentials: true });
      toast.success("Patient deleted successfully");
      fetchPatients();
    } catch (error) {
      toast.error("Failed to delete patient");
    }
  };

  const resetForm = () => {
    setEditingPatient(null);
    setFormData({
      first_name: "",
      last_name: "",
      date_of_birth: "",
      gender: "",
      phone: "",
      email: "",
      address: "",
      insurance_member_id: "",
      insurance_policy_number: "",
      insurance_provider: ""
    });
  };

  const filteredPatients = patients.filter(p => 
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.insurance_member_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="patients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Patients</h1>
            <p className="text-slate-400">Manage your patient records</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                data-testid="add-patient-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Patient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">{editingPatient ? "Edit Patient" : "Add New Patient"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-slate-300">First Name *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      required
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="patient-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-slate-300">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      required
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="patient-last-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth" className="text-slate-300">Date of Birth *</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      required
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="patient-dob"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender" className="text-slate-300">Gender *</Label>
                    <Select 
                      value={formData.gender} 
                      onValueChange={(value) => setFormData({...formData, gender: value})}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="patient-gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-300">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="patient-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="patient-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-slate-300">Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="patient-address"
                  />
                </div>

                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h4 className="font-medium text-white mb-3">Insurance Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="insurance_provider" className="text-slate-300">Insurance Provider</Label>
                      <Select 
                        value={formData.insurance_provider} 
                        onValueChange={(value) => setFormData({...formData, insurance_provider: value})}
                      >
                        <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="patient-insurer">
                          <SelectValue placeholder="Select insurer" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          <SelectItem value="Aetna">Aetna</SelectItem>
                          <SelectItem value="Blue Cross Blue Shield">Blue Cross Blue Shield</SelectItem>
                          <SelectItem value="Cigna">Cigna</SelectItem>
                          <SelectItem value="UnitedHealthcare">UnitedHealthcare</SelectItem>
                          <SelectItem value="Humana">Humana</SelectItem>
                          <SelectItem value="Kaiser Permanente">Kaiser Permanente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="insurance_member_id" className="text-slate-300">Member ID</Label>
                      <Input
                        id="insurance_member_id"
                        value={formData.insurance_member_id}
                        onChange={(e) => setFormData({...formData, insurance_member_id: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                        data-testid="patient-member-id"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label htmlFor="insurance_policy_number" className="text-slate-300">Policy Number</Label>
                    <Input
                      id="insurance_policy_number"
                      value={formData.insurance_policy_number}
                      onChange={(e) => setFormData({...formData, insurance_policy_number: e.target.value})}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="patient-policy-number"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="save-patient-btn">
                    {editingPatient ? "Update Patient" : "Add Patient"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search patients by name or member ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="patient-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Patients Table */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-0">
            {filteredPatients.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">Patient</TableHead>
                    <TableHead className="text-slate-400">DOB</TableHead>
                    <TableHead className="text-slate-400">Gender</TableHead>
                    <TableHead className="text-slate-400">Insurance</TableHead>
                    <TableHead className="text-slate-400">Member ID</TableHead>
                    <TableHead className="text-right text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPatients.map((patient) => (
                    <TableRow key={patient.patient_id} className="border-slate-800 hover:bg-slate-800/50" data-testid={`patient-row-${patient.patient_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-300" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{patient.first_name} {patient.last_name}</p>
                            <p className="text-sm text-slate-400">{patient.email || patient.phone || '-'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-300">{patient.date_of_birth}</TableCell>
                      <TableCell className="capitalize text-slate-300">{patient.gender}</TableCell>
                      <TableCell>
                        {patient.insurance_provider ? (
                          <Badge variant="outline" className="border-slate-600 text-slate-300">{patient.insurance_provider}</Badge>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-300">{patient.insurance_member_id || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(patient)}
                            className="text-slate-400 hover:text-white hover:bg-slate-800"
                            data-testid={`edit-patient-${patient.patient_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(patient.patient_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-patient-${patient.patient_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-400 mb-4">
                  {searchQuery ? "No patients found matching your search" : "No patients yet"}
                </p>
                {!searchQuery && (
                  <Button 
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                    onClick={() => setDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Patient
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
